import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { subscriptionApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import { useSearchParams, useNavigate } from 'react-router-dom'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import Alert from '@/components/ui/Alert'
import CouponApplication from '@/components/subscription/CouponApplication'
import { CreditCard, CheckCircle, XCircle, AlertTriangle, Calendar, DollarSign, ArrowRight } from 'lucide-react'
import { formatDate, formatCurrency } from '@/utils/format'

export default function Subscription() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => subscriptionApi.getStatus(),
    refetchInterval: (query) => {
      // If we have a session_id in URL, poll every 2 seconds for up to 30 seconds
      if (searchParams.get('session_id')) {
        const subscription = (query.state.data?.data as any)?.subscription
        // Stop polling once subscription is active
        if (subscription?.status === 'active') {
          return false
        }
        return 2000 // Poll every 2 seconds
      }
      return false // Don't poll otherwise
    },
  })

  const createCheckoutMutation = useMutation({
    mutationFn: () => subscriptionApi.createCheckout(),
    onSuccess: (response: any) => {
      const url = response?.data?.url
      if (url) {
        window.location.href = url
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to start subscription')
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () => subscriptionApi.cancel(),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
      const cancelDate = response?.data?.cancel_at
      setSuccess(`Subscription will be cancelled on ${cancelDate}. You will retain access until then.`)
      setTimeout(() => setSuccess(''), 5000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to cancel subscription')
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: () => subscriptionApi.reactivate(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
      setSuccess('Subscription reactivated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to reactivate subscription')
    },
  })

  const portalMutation = useMutation({
    mutationFn: () => subscriptionApi.openPortal(),
    onSuccess: (response: any) => {
      const url = response?.data?.url
      if (url) {
        window.location.href = url
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to open billing portal')
    },
  })

  useEffect(() => {
    const sessionId = searchParams.get('session_id')
    if (sessionId) {
      // Immediately invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })

      // Wait a bit for the subscription data to load, then show success message
      const timer = setTimeout(() => {
        const subscription = (data?.data as any)?.subscription
        if (subscription?.status === 'active') {
          setSuccess('Subscription activated successfully! Welcome to BarterSheet Professional.')
          setTimeout(() => setSuccess(''), 5000)
          // Clean up URL after showing success message
          const newSearchParams = new URLSearchParams(searchParams)
          newSearchParams.delete('session_id')
          setSearchParams(newSearchParams, { replace: true })
        }
      }, 1000)

      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]) // Run when data changes to detect when subscription becomes active

  const subscription = (data?.data as any)?.subscription
  const userStatus = (data?.data as any)?.user_status

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel your subscription? You will lose access at the end of your billing period.')) {
      cancelMutation.mutate()
    }
  }

  if (isLoading) return <Loading />

  // Admin or super admin users don't need subscriptions
  if (user?.user_type === 'admin' || user?.user_type === 'super_admin') {
    return (
      <div className="max-w-4xl mx-auto">
        <Alert
          type="info"
          message="Your admin account has full access to BarterSheet without requiring a subscription."
        />
      </div>
    )
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1 inline" /> Active</Badge>
      case 'cancelled':
        return <Badge variant="danger"><XCircle className="w-3 h-3 mr-1 inline" /> Cancelled</Badge>
      case 'past_due':
        return <Badge variant="warning"><AlertTriangle className="w-3 h-3 mr-1 inline" /> Past Due</Badge>
      case 'inactive':
        return <Badge variant="gray">Inactive</Badge>
      default:
        return <Badge variant="gray">{status}</Badge>
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-3xl font-bold">Subscription Management</h1>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Current Status */}
      <Card title="Subscription Status">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Current Status</p>
              <div className="mt-1">
                {getStatusBadge(userStatus || 'inactive')}
              </div>
            </div>
            {subscription && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Monthly Fee</p>
                <p className="text-2xl font-bold text-primary-600">
                  {formatCurrency(subscription.amount)}/mo
                </p>
              </div>
            )}
          </div>

          {subscription && subscription.status === 'active' && (
            <>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-gray-500 flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    Current Period Start
                  </p>
                  <p className="font-medium mt-1">
                    {formatDate(subscription.current_period_start)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    Next Billing Date
                  </p>
                  <p className="font-medium mt-1">
                    {formatDate(subscription.current_period_end)}
                  </p>
                </div>
              </div>

              {subscription.cancel_at_period_end && (
                <Alert
                  type="warning"
                  message={
                    <div>
                      <p className="font-semibold">Subscription Scheduled for Cancellation</p>
                      <p className="text-sm mt-1">
                        Your subscription will end on {formatDate(subscription.current_period_end)}.
                        You can reactivate it anytime before then.
                      </p>
                    </div>
                  }
                />
              )}
            </>
          )}

          {subscription && subscription.status === 'past_due' && (
            <Alert
              type="error"
              message="Your subscription payment failed. Please update your payment method to continue using BarterSheet."
            />
          )}

          {(!subscription || subscription.status === 'inactive' || subscription.status === 'cancelled') && (
            <Alert
              type="warning"
              message="You need an active subscription to access BarterSheet features. Subscribe now to continue using the platform."
            />
          )}
        </div>
      </Card>

      {/* Actions */}
      <Card>
        <div className="space-y-4">
          {!subscription || subscription.status === 'inactive' || subscription.status === 'cancelled' ? (
            <div>
              <h3 className="text-lg font-semibold mb-4">Subscribe to BarterSheet Professional</h3>
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-6 rounded-lg border border-primary-200 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-primary-900">$5.00 / month</p>
                    <p className="text-sm text-primary-700 mt-1">Full access to the BarterSheet platform</p>
                  </div>
                  <CreditCard className="w-12 h-12 text-primary-400" />
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6 mb-4">
                <h4 className="font-semibold mb-3">What's included:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Create and manage unlimited barters</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Access to barter groups and collaborative exchanges</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Buy, sell, and transfer BarterCoins</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Secure escrow protection for all transactions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Direct messaging and group chat features</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Priority customer support</span>
                  </li>
                </ul>
              </div>

              {/* Coupon Application */}
              <div className="mb-6">
                <CouponApplication />
              </div>

              <div className="space-y-3">
                <Button
                  className="w-full"
                  size="lg"
                  onClick={() => createCheckoutMutation.mutate()}
                  isLoading={createCheckoutMutation.isPending}
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Subscribe Now - $5.00/month
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>

                <Button
                  variant="secondary"
                  className="w-full"
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                >
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Continue with Free Tier
                </Button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-sm mb-2">Free Tier Includes:</h4>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>1 active barter listing</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>2 inactive barter listings</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Basic barter coin features</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Community support</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500 mt-3 italic">Upgrade anytime to unlock unlimited barters and premium features!</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {subscription.status === 'active' && (
                <>
                  {!subscription.cancel_at_period_end ? (
                    <>
                      <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => portalMutation.mutate()}
                        isLoading={portalMutation.isPending}
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        Manage Billing & Payment Method
                      </Button>
                      <Button
                        variant="danger"
                        className="w-full"
                        onClick={handleCancel}
                        isLoading={cancelMutation.isPending}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Subscription
                      </Button>
                    </>
                  ) : (
                    <Button
                      className="w-full"
                      onClick={() => reactivateMutation.mutate()}
                      isLoading={reactivateMutation.isPending}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Reactivate Subscription
                    </Button>
                  )}
                </>
              )}

              {subscription.status === 'past_due' && (
                <Button
                  className="w-full"
                  onClick={() => portalMutation.mutate()}
                  isLoading={portalMutation.isPending}
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Update Payment Method
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="text-sm text-blue-900">
          <p className="font-semibold mb-2">Subscription Information:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Cancel anytime with no penalties</li>
            <li>Access continues until the end of your billing period</li>
            <li>Secure payments processed by Stripe</li>
            <li>All prices in USD</li>
          </ul>
        </div>
      </Card>
    </div>
  )
}
